import React, { useEffect, useState } from 'react';
import { Alert, Button, Spin, Tag } from 'antd';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchSupplierOrderDelivery } from '../api/supplierOrderDeliveryApi';
import type { SupplierOrderDelivery, DeliveryDocument, DeliveryInvoice } from '../../../types/supplierOrderDelivery';
import { formatUnit } from '../../../utils/units';
import { formatLocalizedQuantity } from '../../../utils/numberFormatting';
import { formatDateEuropean } from '../../../utils/dateUtils';
import { getTransportLabel } from '../../../utils/transportLabel';
import ProductNameWithDimensions, { type ProductDimensionLine } from '../../../components/products/ProductNameWithDimensions';
import './SupplierOrderDeliveries.css';

interface Props {
  supplierOrderId: string; revision: unknown; actions: React.ReactNode;
  onOpenInvoice: (id: string) => void;
  onDeliveryInvoice: (transportId: string, invoiceId: string, receiptOnly?: boolean, transportLabel?: string, truckOpenQuantities?: Record<string, number>) => void;
  /** ZD lines of the card (a delivery line id is the ZD line id): their dimensions under the name. */
  items?: ProductDimensionLine[];
}
export default function SupplierOrderDeliveries({ supplierOrderId, revision, actions, onOpenInvoice, onDeliveryInvoice, items }: Props) {
  const { t } = useTranslation(['orders', 'transport', 'pz']);
  const [data, setData] = useState<SupplierOrderDelivery | null>(null);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const label = (key: string) => t(`orders:supplierOrders.deliveries.${key}`);
  useEffect(() => {
    let active = true; setData(null); setFailed(false);
    fetchSupplierOrderDelivery(supplierOrderId).then(result => { if (active) setData(result); })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [supplierOrderId, revision, retry]);
  const amount = (n: number | null, unit: string) => n === null ? '—' : `${formatLocalizedQuantity(n, 'pl')} ${formatUnit(unit)}`;
  const invoiceLinks = (rows: DeliveryInvoice[], unit?: string, truckDone = false) => rows.map((doc, index) => (
    <div key={`${doc.id}-${index}`} className="zd-deliveries__document">
      <Button type="link" size="small" title={doc.number || label('draft')} onClick={() => onOpenInvoice(doc.id)}>{doc.number || label('draft')}</Button>
      <small>{label(doc.status === 'DRAFT' ? 'draft' : doc.corrected ? 'corrected' : 'approved')}{unit ? ` · ${amount(doc.quantity, unit)}` : ''}
        {doc.shared ? ` · ${label('sharedInvoice')}` : ''}
        {unit && doc.unreceived && (doc.shared || !truckDone) ? ` · ${label('unreceived')} ${amount(doc.unreceived, unit)}` : ''}</small>
    </div>
  ));
  const pzLinks = (rows: DeliveryDocument[], unit: string) => rows.map((doc, index) => (
    <div key={`${doc.id}-${index}`} className="zd-deliveries__document">
      <Link to={`/app/pz/${encodeURIComponent(doc.id)}`} title={doc.number}>{doc.number}</Link>
      <small>{doc.received ? label('receivedDocument') : t(`pz:status.${doc.status.toLowerCase()}`, { defaultValue: label('notReceived') })} · {amount(doc.quantity, unit)}
        {doc.transportIds.length > 1 ? ` · ${label('sharedDocument')}` : ''}</small>
    </div>
  ));
  // What each ZD line of one truck still awaits on a PZ (collective invoice received truck by truck).
  const truckOpenQuantities = (transportId: string) => Object.fromEntries((data?.lines || []).flatMap(line => line.assignments
    .filter(row => row.id === transportId && row.quantity !== null && !row.ambiguous)
    .map(row => [line.id, Math.max(0, row.quantity! - (row.received ?? 0))])));
  // The truck received its whole load: nothing more to receive on it.
  const isTruckDone = (row: SupplierOrderDelivery['lines'][number]['assignments'][number]) =>
    row.quantity !== null && row.received !== null && row.received >= row.quantity;
  // A shared invoice covers this truck only with what it has not yet received.
  const coverage = (invoice: DeliveryInvoice) => invoice.shared ? invoice.unreceived ?? null : invoice.quantity;
  return (
    <section className="zd-deliveries" data-testid="supplier-order-deliveries">
      <header className="zd-deliveries__header">
        <div><h3>{label('title')}</h3><p>{label('hint')}</p></div>
        <div className="zd-deliveries__actions">{actions}</div>
      </header>
      {failed ? <Alert type="warning" showIcon message={label('loadFailed')} action={<Button onClick={() => setRetry(v => v + 1)}>{label('retry')}</Button>} /> : !data ? <Spin /> : (
        <>
          {data.lines.map(line => (
            <article className="zd-deliveries__line" key={line.id} data-testid="supplier-delivery-line">
              <div className="zd-deliveries__summary">
                <h4><ProductNameWithDimensions name={line.name} dimensions={items?.find(item => item.id === line.id)}
                  dimensionsTestId="supplier-delivery-line-dimensions" /></h4>
                <dl>{(['ordered', 'delivered', 'received', 'remaining', 'unassigned'] as const).map(key => (
                  <div key={key} className={key === 'unassigned' && (line[key] ?? 0) > 0 ? 'is-unassigned' : ''}>
                    <dt>{label(key)}</dt><dd data-testid={`delivery-${key}`}>{amount(line[key], line.unit)}</dd>
                  </div>
                ))}</dl>
              </div>
              {line.assignments.length ? (
                <div className="zd-deliveries__table-wrap">
                  <table className="zd-deliveries__table">
                    <thead><tr>{['transport', 'quantity', 'dateStatus', 'invoice', 'pz'].map(key => <th key={key}>{label(key)}</th>)}</tr></thead>
                    <tbody>{line.assignments.map(assignment => (
                      <tr key={assignment.id} data-testid="supplier-delivery-assignment">
                        <td><Link className="zd-deliveries__transport" to={`/app/transports/${encodeURIComponent(assignment.id)}`}>{getTransportLabel(assignment)}</Link>
                          {(() => {
                            const route = `${assignment.loadingAddresses.join(', ') || '—'} → ${assignment.deliveryAddresses.join(', ') || '—'}`;
                            return <small className="zd-deliveries__route" title={route}>{route}</small>;
                          })()}
                        </td>
                        <td>{amount(assignment.quantity, line.unit)}{assignment.ambiguous ? <small>{label('ambiguous')}</small> : null}</td>
                        <td><span>{assignment.date ? formatDateEuropean(assignment.date) : '—'}</span><small>
                          <Tag color={assignment.status === 'DELIVERED' ? 'green' : assignment.status === 'IN_TRANSIT' ? 'blue' : 'default'}>
                            {t(`transport:status.${assignment.status.toLowerCase()}`, { defaultValue: label('unknown') })}
                          </Tag>
                        </small></td>
                        <td>{assignment.invoices === undefined ? '—' : assignment.invoices.length ? invoiceLinks(assignment.invoices, line.unit, isTruckDone(assignment))
                          : <span className={assignment.status === 'DELIVERED' ? 'zd-deliveries__warning' : 'zd-deliveries__muted'}>{label('noLinkedInvoice')}</span>}
                          {assignment.invoices !== undefined && <div className="zd-deliveries__row-actions">
                            {assignment.invoices.some(invoice => invoice.status === 'DRAFT')
                              ? assignment.invoices.filter(invoice => invoice.status === 'DRAFT').map(invoice => <Button key={invoice.id} size="small" onClick={() => onDeliveryInvoice(assignment.id, invoice.id, false, getTransportLabel(assignment))}>{label('continueInvoice')}</Button>)
                              : assignment.quantity !== null && !assignment.ambiguous && assignment.invoices.every(invoice => coverage(invoice) !== null)
                                && assignment.invoices.reduce((sum, invoice) => sum + (coverage(invoice) || 0), 0) < assignment.quantity
                                ? <Button size="small" onClick={() => onDeliveryInvoice(assignment.id, 'new', false, getTransportLabel(assignment))}>{label('addInvoice')}</Button> : null}
                          </div>}
                        </td>
                        <td>{assignment.documents.length ? pzLinks(assignment.documents, line.unit) : <span className="zd-deliveries__muted">
                          {label(assignment.invoices === undefined ? 'noPz' : assignment.invoices.some(invoice => invoice.status === 'APPROVED') ? 'awaitingPz' : line.invoices?.length ? 'checkLineInvoices' : 'invoiceRequired')}
                        </span>}
                          {(() => {
                            const open = (assignment.invoices || []).filter(invoice => invoice.status === 'APPROVED' && (invoice.unreceived ?? 0) > 0 && !isTruckDone(assignment));
                            return open.map(invoice => <div key={invoice.id} className="zd-deliveries__row-actions"><Button size="small" type="primary"
                              onClick={() => onDeliveryInvoice(assignment.id, invoice.id, true, getTransportLabel(assignment), truckOpenQuantities(assignment.id))}>
                              {open.length > 1 ? `${label('addPz')} · ${invoice.number}` : label('addPz')}</Button></div>);
                          })()}
                        </td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              ) : <p className="zd-deliveries__empty">{label('noTransports')}</p>}
              {(line.invoices?.length || line.documents.length) ? <div className="zd-deliveries__unlinked">
                <span>{label('unlinkedDocuments')}</span>{invoiceLinks(line.invoices || [], line.unit)}{pzLinks(line.documents, line.unit)}
              </div> : null}
            </article>
          ))}
          {!!data.invoices?.length && <div className="zd-deliveries__unlinked" data-testid="delivery-unassigned-invoices">
            <span>{label('unassignedInvoices')}</span>{invoiceLinks(data.invoices)}
          </div>}
          <footer className="zd-deliveries__footnote">{label('footnote')}</footer>
        </>
      )}
    </section>
  );
}
